/*
 * Passkey registration — the same dialog for Add and for Update.
 *
 * "Update" here does not replace anything. Credentials accumulate on the
 * account, so the row in the factor list means "this user has Passkey", not
 * "this user has one passkey"; every string on the update path says *added*.
 *
 * Two browser-side outcomes are not failures and must not be reported as such:
 *
 *   NotAllowedError    the user dismissed the OS prompt (or it timed out).
 *                      Nothing happened. Return to the intro silently — an
 *                      error banner for a deliberate cancel trains people to
 *                      ignore the banner.
 *   InvalidStateError  beginAuthRegistration sent this user's existing
 *                      credentials as `excludeCredentials`, so the browser
 *                      refused to register the same authenticator twice. It
 *                      fails locally, before any request goes out — "something
 *                      went wrong" would send the user to retry the one thing
 *                      that cannot work.
 */
import { useEffect, useState } from "react";
import { Modal, Spin } from "antd";
import { AN_COLORS, AN_FONT } from "../../../design-system/tokens";
import { logOutUser } from "../../../common";
import { dispatchBeginAuthRegistration, dispatchFinishRegistration } from "../../../utils/apiservice";
import { passkeysAvailable, toAttestationPayload, toCredentialCreationOptions } from "../../../utils/webauthn";
import { btn, Callout } from "./mfaCommon";

export default function PasskeySetupDialog({ open, mode, email, url, onClose, onEnrolled }) {
  const isUpdate = mode === "update";
  const supported = passkeysAvailable();

  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setRunning(false);
    setError("");
  }, [open]);

  const fail = (msg) => { setRunning(false); setError(msg); };

  const finish = (credential) => {
    dispatchFinishRegistration({
      payload: { email, url, credential: toAttestationPayload(credential) },
      onSuccess: (res) => {
        setRunning(false);
        if (res?.success === false) { setError(res?.message || "The passkey could not be registered."); return; }
        onEnrolled?.({ message: isUpdate ? "Passkey added to your account." : "Passkey added." });
      },
      onFailure: ({ message: m, isLogout }) => {
        if (isLogout) { setRunning(false); logOutUser(); return; }
        fail(m || "The passkey could not be registered.");
      },
      onError: ({ message: m }) => fail(m || "The passkey could not be registered."),
    });
  };

  const start = () => {
    setRunning(true);
    setError("");
    dispatchBeginAuthRegistration({
      payload: { email, url },
      onSuccess: async (res) => {
        const publicKey = res?.publicKey;
        if (!publicKey?.challenge) { fail("The server did not return a registration challenge."); return; }
        let credential;
        try {
          credential = await navigator.credentials.create({
            publicKey: toCredentialCreationOptions(publicKey),
          });
        } catch (err) {
          // Branch on the DOMException name, never on its message — the text
          // is browser-specific and localised.
          if (err?.name === "NotAllowedError") { setRunning(false); return; }
          if (err?.name === "InvalidStateError") {
            fail("This device already has a passkey for your account — try a different device or security key.");
            return;
          }
          fail(err?.message || "Your browser could not create a passkey on this device.");
          return;
        }
        if (!credential) { setRunning(false); return; }
        finish(credential);
      },
      onFailure: ({ message: m, isLogout }) => {
        if (isLogout) { setRunning(false); logOutUser(); return; }
        fail(m || "Could not start passkey registration.");
      },
      onError: ({ message: m }) => fail(m || "Could not start passkey registration."),
    });
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={440}
      title={
        <span style={{ fontFamily: AN_FONT, fontSize: 15, fontWeight: 600, color: AN_COLORS.ink }}>
          {isUpdate ? "Add another passkey" : "Add a passkey"}
        </span>
      }
    >
      <div style={{ fontFamily: AN_FONT }}>
        {!supported ? (
          <>
            <Callout tone="warn">
              This browser can&rsquo;t create passkeys. Passkeys need an up-to-date browser on a secure
              (https) connection — try Chrome, Edge, or Safari on this device.
            </Callout>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
              <button type="button" style={btn("secondary")} onClick={onClose}>Close</button>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 13, color: AN_COLORS.ink2, lineHeight: 1.6 }}>
              Your browser will ask for your fingerprint, face, screen lock, or a security key. The passkey
              is created on the device you are using now.
            </div>

            {isUpdate && (
              <div style={{ marginTop: 14 }}>
                <Callout>
                  Your existing passkeys keep working — this adds another device to your account, it does
                  not replace anything.
                </Callout>
              </div>
            )}

            {error && <div style={{ marginTop: 14 }}><Callout tone="danger">{error}</Callout></div>}

            {running ? (
              <div style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "18px 0 4px", fontSize: 12.5, color: AN_COLORS.muted,
              }}>
                <Spin size="small" />
                Waiting for your device…
              </div>
            ) : null}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
              <button type="button" style={btn("secondary")} onClick={onClose}>Cancel</button>
              <button
                type="button"
                onClick={start}
                disabled={running}
                style={{ ...btn("primary"), opacity: running ? 0.5 : 1, cursor: running ? "not-allowed" : "pointer" }}
              >
                {isUpdate ? "Add another device" : "Set up passkey"}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
